import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import classNames from 'classnames';

import '../css/SatelliteTab.less';
import Auth from '../managers/AuthManager';
import { register } from '../i18n';

import { SatCoreRegister } from '../SatCoreRegistry';

import ContentService from '../services/ContentService';

import { focusableElementList } from '../utils';

const t = register('SatelliteTab');

export default
@inject('contentManager', 'courseManager', 'classroomManager', 'userManager')
@observer
class SatelliteTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      leftSide: true,
      tabOpen: false,
      emptyRelatedContent: false,
    };

    this.contentPanelRef = React.createRef();
  }

  async componentDidMount() {
    window.removeEventListener('message', this.handleMessage);
    window.addEventListener('message', this.handleMessage);

    const { contentManager } = this.props;
    this.wasLessonPlayerShowing = contentManager.lessonPlayerShowing;

    // Check initial state and notify iframe if conditions are met
    this.notifyIframeButtonVisibility(false,false);
  }

  componentDidUpdate() {
    const { contentManager } = this.props;
    const { lessonPlayerShowing } = contentManager;
    if (this.wasLessonPlayerShowing && !lessonPlayerShowing) {
      this.resetRelatedContentTab();
    }
    this.wasLessonPlayerShowing = lessonPlayerShowing;
  }

  resetRelatedContentTab = () => {
    const { courseManager } = this.props;
    ContentService.setSatelliteTabShowing(false);
    courseManager.setRelatedContentElementList([]);
    courseManager.setRelatedLinkedContentElementList([]);
    this.setState({ tabOpen: false, emptyRelatedContent: false });
    this.notifyIframeButtonVisibility(false, false);
  }
  
  notifyIframeButtonVisibility = (show = null,  open = null) => {
  
    const { contentManager, courseManager } = this.props;
    const { lessonPlayerShowing } = contentManager;
    const shouldShowButton = show !== null ? show : (lessonPlayerShowing && 
                             courseManager.relatedContentElementList && 
                             courseManager.relatedContentElementList.length > 0);
    
    const iframe = document.getElementById('fulldialog-frame');
    if (iframe && iframe.contentWindow) {
      // console.log('notifying iframe button visibility', shouldShowButton);
      // console.log('open', open);
      iframe.contentWindow.postMessage({
        type: 'related_tab_button_visibility',
        show: shouldShowButton,
        open: open
      }, Auth.lesson);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  notifySatelliteContentUnavailable = () => {
    const iframe = document.getElementById('fulldialog-frame');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'satellite_content_unavailable',
        open: true
      }, Auth.lesson);
    }
  }

  applyRelatedContentResult = (hasRelated) => {
    const { tabOpen } = this.state;
    if (hasRelated) {
      ContentService.setSatelliteTabShowing(true);
      this.setState({ emptyRelatedContent: false });
      this.notifyIframeButtonVisibility(true, tabOpen);
      return;
    }
    if (tabOpen) {
      // Keep the overlay open with an empty state until the lesson iframe
      // either switches to another left tab or confirms this empty panel.
      ContentService.setSatelliteTabShowing(true);
      this.setState({ emptyRelatedContent: true });
      this.notifySatelliteContentUnavailable();
      return;
    }
    ContentService.setSatelliteTabShowing(false);
    this.setState({ emptyRelatedContent: false });
    this.notifyIframeButtonVisibility(false, false);
  }

  handleMessage = async (event) => {
    const { contentManager, courseManager, userManager, classroomManager } = this.props;
    const { isStudent } = userManager;

    if (event.origin !== Auth.lesson || !event.data || !event.data.type) {
      return;
    }

    if (!contentManager.lessonPlayerShowing) {
      return;
    }

    // clear out the related linked content element list. this sometimes shows, releated content always shows.
    await courseManager.setRelatedLinkedContentElementList([]);
    if (event.data.type === 'current_elements' && !isStudent) {
      if (event.data.lessonElementIds) {
        await courseManager.viewRealatedContent(event.data.lessonElementIds, classroomManager.currentClassroomId);
      } else {
        courseManager.setRelatedContentElementList([]);
      }
      const hasRelated = !!(courseManager.relatedContentElementList &&
        courseManager.relatedContentElementList.length > 0);
      this.applyRelatedContentResult(hasRelated);
    }

    // Dedicated satellite tab control messages
    if (event.data.type === 'open_satellite') {
      ContentService.setSatelliteTabShowing(true);
      this.setState({ tabOpen: true, emptyRelatedContent: false }, () => {
        if (this.contentPanelRef.current) {
          const firstFocusable = this.contentPanelRef.current.querySelector(focusableElementList);
          firstFocusable?.focus();
        }
      });
    }

    if (event.data.type === 'close_satellite') {
      // Keep the panel mounted so width can collapse with the iframe tab strip.
      this.setState({ tabOpen: false, emptyRelatedContent: false });
    }

    if (event.data.type === 'satellite_empty') {
      ContentService.setSatelliteTabShowing(true);
      this.setState({ tabOpen: true, emptyRelatedContent: true });
    }

    if (event.origin === Auth.lesson && event.data.type && event.data.type === 'linked_element' && !isStudent) {
      const { foreverId } = event.data;

      // Find matching element in relatedContentElementList
      const matchingElement = courseManager.relatedContentElementList.find(
        (relatedElement) => relatedElement.contentItemForeverId === foreverId
      );

      if (matchingElement) {
        // Populate relatedLinkedContentElementList with the matching element
        courseManager.setRelatedLinkedContentElementList([matchingElement]);
        ContentService.setSatelliteTabShowing(true);
        this.notifyIframeButtonVisibility(true,true);
        // Open the satellite tab programmatically (no button)
        this.setState({ tabOpen: true, emptyRelatedContent: false }, () => {
          if (this.contentPanelRef.current) {
            const firstFocusable = this.contentPanelRef.current.querySelector(focusableElementList);
            firstFocusable?.focus();
          }
        });
      }
    }
    if (event.data.type === 'access_assignments' && isStudent) {
      // Keep the overlay mounted while it is open so navigation does not close it.
      ContentService.setSatelliteTabShowing(!!event.data.previousAssignments || this.state.tabOpen);
      this.notifyIframeButtonVisibility(event.data.previousAssignments, this.state.tabOpen);
    }
  }

  render() {
    const { additionalClassName = '', ChildComponent, tabName, contentManager, courseManager, userManager } = this.props;
    const { lessonPlayerShowing } = contentManager;
    const { isStudent } = userManager;
    const { leftSide, tabOpen, emptyRelatedContent } = this.state;

    let satelliteTabClassName = 'satelliteTab';
    satelliteTabClassName += additionalClassName ? ` ${additionalClassName}` : '';

    const hasRelatedContent = !!(courseManager.relatedContentElementList &&
      courseManager.relatedContentElementList.length > 0);
    const showTeacherPanel = !isStudent && ChildComponent && lessonPlayerShowing &&
      contentManager.satelliteTabShowing && (hasRelatedContent || emptyRelatedContent || tabOpen);

    if (showTeacherPanel) {
      return (
        <div className={classNames({ left: leftSide, open: tabOpen }, satelliteTabClassName)}>
          <div ref={this.contentPanelRef} className={classNames('contentPanel', 'relatedContent', {
            emptyRelatedContent
          })}>
            {emptyRelatedContent ? (
              <div className='satellite-empty-message'>
                {t('noRelatedContent', 'No related content for this item')}
              </div>
            ) : (
              <ChildComponent />
            )}
          </div>
          
        </div>
      );
    }
    if (isStudent && ChildComponent !== null && ChildComponent !== undefined && lessonPlayerShowing && contentManager.satelliteTabShowing) {
      return (
        <div className={classNames({ left: leftSide, open: tabOpen }, satelliteTabClassName)}>
          <div ref={this.contentPanelRef} className={classNames('contentPanel', 'pastAssignments')}>
            <ChildComponent />
          </div>
          
        </div>
      );
    }

    return (
      <></>
    );
  }
}

SatCoreRegister('SatelliteTab', SatelliteTab);
